import React, { useState, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { dateOnlyBodyTemplate } from '../../../../../utils/dateUtils';
import { PAGE_SIZE_OPTIONS } from '../../../../../utils/tableConstants';
import GenericFileExportButton from '../../../../../components/common/GenericFileExportButton';
import { useExportDealerInvoiceToExcelMutation } from '../../../../../api/endpoints/dealerInvoiceEntryApi';
import { InvoiceStatus } from '../constants/invoiceStatus';
import { TAB_IDS } from '../constants/invoiceTabs';

// Garanti ekibi bekleme kolonlarının görüneceği sekmeler
const WAITING_DAYS_TABS = [TAB_IDS.PENDING, TAB_IDS.REJECTED, TAB_IDS.APPROVED, TAB_IDS.ALL_GT_ORDERS];
const DEALER_WAITING_DAYS_TABS = [TAB_IDS.SAP_RECORDS, TAB_IDS.RETURNED, TAB_IDS.ALL_GT_ORDERS];

// Tüm GT Siparişleri karışık durumlu olduğundan hangi bekleme süresinin gösterileceği satır durumuna göre belirlenir
const WAITING_DAYS_STATUSES = [InvoiceStatus.PENDING, InvoiceStatus.REJECTED, InvoiceStatus.APPROVED];
const isDealerWaitingStatus = (status) => status == null || status === InvoiceStatus.RETURNED;

// --- Action Buttons ---
const ActionButtons = ({ rowData, actions, activeTabId, isWarrantyTeam, th }) => {
    return (
        <div className="flex gap-2 align-items-center justify-content-center">
            {/* SAP Kayıtları (Tab 0) - Sadece Oluştur Butonu */}
            {activeTabId === TAB_IDS.SAP_RECORDS && !isWarrantyTeam && (
                <Button
                    icon="pi pi-plus"
                    className="p-button-rounded p-button-text p-button-success"
                    tooltip={th('dealerInvoice.createEntryTooltip')}
                    onClick={(e) => { e.stopPropagation(); actions.onCreate(rowData); }}
                />
            )}
            {/* Garanti Ekibi İşlemleri (Onay/Red/İade) */}
            {activeTabId === TAB_IDS.PENDING && isWarrantyTeam && (
                <>
                    <Button icon="pi pi-check-circle" className="p-button-rounded p-button-text p-button-success" tooltip={th('warrantyTeam.approve')} onClick={(e) => { e.stopPropagation(); actions.onApprove(rowData); }} />
                    <Button icon="pi pi-times-circle" className="p-button-rounded p-button-text p-button-danger" tooltip={th('warrantyTeam.reject')} onClick={(e) => { e.stopPropagation(); actions.onReject(rowData); }} />
                    <Button icon="pi pi-replay" className="p-button-rounded p-button-text p-button-warning" tooltip={th('warrantyTeam.return')} onClick={(e) => { e.stopPropagation(); actions.onReturn(rowData); }} />
                </>
            )}
            {/* Düzenleme (Sadece İade edilenler veya Taslaklar için) */}
            {(activeTabId === TAB_IDS.RETURNED || activeTabId === TAB_IDS.DRAFT) && !isWarrantyTeam && (
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-text p-button-info"
                    tooltip={th('common.edit')}
                    onClick={(e) => { e.stopPropagation(); actions.onEdit(rowData); }}
                />
            )}
            {/* Geçmiş Butonu (SAP Tabı hariç her yerde) */}
            {activeTabId !== TAB_IDS.SAP_RECORDS && (
                <Button
                    icon="pi pi-history"
                    className="p-button-rounded p-button-text p-button-secondary"
                    tooltip={th('common.history')}
                    onClick={(e) => { e.stopPropagation(); actions.onHistory(rowData); }}
                />
            )}

            <Button
                icon="pi pi-list"
                className="p-button-rounded p-button-text p-button-info"
                tooltip={th('dealerInvoice.viewClaimsDetail')}
                onClick={(e) => { e.stopPropagation(); actions.onViewDetail(rowData); }}
            />
        </div>
    );
};


const DealerInvoiceTable = ({
    items, loading, totalRecords, lazyParams, onPage, onSort,
    activeTabId,
    handleCreate, handleEdit, handleShowHistory,
    handleApprove, handleReject, handleReturn,
    handleViewDetail,
    isWarrantyTeam, th,
    exportParams
}) => {
    const [actionDialog, setActionDialog] = useState({ visible: false, type: null, record: null });
    const [actionReason, setActionReason] = useState('');

    const isSapTab = activeTabId === TAB_IDS.SAP_RECORDS;
    const isAllGTTab = activeTabId === TAB_IDS.ALL_GT_ORDERS;
    const isDraftTab = activeTabId === TAB_IDS.DRAFT;

    // --- Body Templates ---
    const currencyBody = (rowData) => {
        const val = rowData.totalPrice ?? rowData.totalAmount ?? 0;
        const cur = rowData.currency || 'TRY';
        try {
            return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: cur }).format(val);
        } catch {
            return `${val} ${cur}`;
        }
    };

    const statusBody = (rowData) => {
        const statuses = {
            [InvoiceStatus.DRAFT]: { label: th('dealerInvoice.tabs.draft'), severity: 'secondary' },
            [InvoiceStatus.PENDING]: { label: th('dealerInvoice.tabs.pending'), severity: 'warning' },
            [InvoiceStatus.RETURNED]: { label: th('dealerInvoice.tabs.returned'), severity: 'info' },
            [InvoiceStatus.REJECTED]: { label: th('dealerInvoice.tabs.rejected'), severity: 'danger' },
            [InvoiceStatus.APPROVED]: { label: th('dealerInvoice.tabs.approved'), severity: 'success' }
        };
        if (!rowData.status && rowData.status !== 0) return null;
        const status = statuses[rowData.status];
        if (!status) return <Tag value={rowData.status} severity="secondary" />;
        return <Tag value={status.label} severity={status.severity} />;
    };

    const waitingDaysBody = (rowData) => {
        if (isAllGTTab && !WAITING_DAYS_STATUSES.includes(rowData.status)) return '-';
        return rowData.waitingDays ?? '-';
    };

    const dealerWaitingDaysBody = (rowData) => {
        if (isAllGTTab && !isDealerWaitingStatus(rowData.status)) return '-';
        return rowData.dealerWaitingDays ?? '-';
    };

    const warrantyTypeBody = (rowData) => {
        if (!rowData.warrantyType) return '-';
        return <Tag value={rowData.warrantyType} severity="info" />;
    };

    // --- Action Dialog ---
    const openActionDialog = (type, record) => {
        setActionDialog({ visible: true, type, record });
        setActionReason('');
    };

    const closeActionDialog = () => {
        setActionDialog({ visible: false, type: null, record: null });
        setActionReason('');
    };

    const confirmAction = () => {
        const { type, record } = actionDialog;
        if (type === 'approve') handleApprove(record, actionReason);
        else if (type === 'reject') handleReject(record, actionReason);
        else if (type === 'return') handleReturn(record, actionReason);
        closeActionDialog();
    };

    const actions = useMemo(() => ({
        onCreate: handleCreate,
        onEdit: handleEdit,
        onHistory: handleShowHistory,
        onApprove: (row) => openActionDialog('approve', row),
        onReject: (row) => openActionDialog('reject', row),
        onReturn: (row) => openActionDialog('return', row),
        onViewDetail: handleViewDetail
    }), [handleCreate, handleEdit, handleShowHistory, handleViewDetail]);

    const showActionsColumn = true;

    const tableHeader = (
        <div className="flex justify-content-end">
            <GenericFileExportButton
                useExportQuery={useExportDealerInvoiceToExcelMutation}
                exportParams={exportParams || {}}
                buttonLabel={th('common.export') || "Excel'e Aktar"}
                buttonClassName="p-button-success p-button-sm"
                icon="pi pi-file-excel"
                disabled={!items || items.length === 0}
                defaultFileName="BayiFaturaListesi"
            />
        </div>
    );

    return (
        <>
            <DataTable
                value={items}
                lazy
                paginator
                first={lazyParams.first}
                rows={lazyParams.rows}
                totalRecords={totalRecords}
                onPage={onPage}
                onSort={onSort}
                sortField={lazyParams.sortField}
                sortOrder={lazyParams.sortOrder}
                loading={loading}
                emptyMessage={th('common.noRecords') || 'Kayıt bulunamadı'}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                dataKey={isSapTab ? "groupId" : "recordId"}
                className="p-datatable-gridlines"
                stripedRows
                rowHover
                header={tableHeader}
            >
                {showActionsColumn && (
                    <Column
                        body={(rowData) => (
                            <ActionButtons
                                rowData={rowData}
                                actions={actions}
                                activeTabId={activeTabId}
                                isWarrantyTeam={isWarrantyTeam}
                                th={th}
                            />
                        )}
                        header={th('common.actions') || 'İşlemler'}
                        alignHeader="center"
                        style={{ width: '180px', textAlign: 'center' }}
                    />
                )}

                <Column
                    field="groupId"
                    header={th('dealerInvoice.groupId')}
                    sortable
                    style={{ minWidth: '130px' }}
                />

                {/* Bayi Kodu ve Bayi Adı: Garanti ekibi veya Tüm GT Siparişleri */}
                {(isWarrantyTeam || isAllGTTab) && (
                    <Column
                        field="dealerCode"
                        header={th('warrantyTeam.dealerCode')}
                        sortable
                        style={{ minWidth: '110px' }}
                    />
                )}
                {(isWarrantyTeam || isAllGTTab) && (
                    <Column
                        field="dealerName"
                        header={th('warrantyTeam.dealerName')}
                        sortable
                        style={{ minWidth: '180px' }}
                    />
                )}

                {!isSapTab && (
                    <Column
                        field="invoiceNumber"
                        header={th('dealerInvoice.invoiceNumber')}
                        sortable
                        style={{ minWidth: '140px' }}
                    />
                )}
                {!isSapTab && (
                    <Column
                        field="invoiceDate"
                        header={th('dealerInvoice.invoiceDate')}
                        body={(r) => dateOnlyBodyTemplate(r, 'invoiceDate')}
                        sortable
                        style={{ minWidth: '120px' }}
                    />
                )}
                {isDraftTab && (
                    <Column
                        field="createdDate"
                        header={th('dealerInvoice.createdDate')}
                        body={(r) => dateOnlyBodyTemplate(r, 'createdDate')}
                        sortable
                        style={{ minWidth: '120px' }}
                    />
                )}

                <Column
                    field="totalPrice"
                    header={th('dealerInvoice.totalPrice')}
                    body={currencyBody}
                    sortable
                    align="right"
                    style={{ minWidth: '130px' }}
                />

                <Column
                    field="warrantyType"
                    header={th('dealerInvoice.warrantyType') || 'Garanti Tipi'}
                    body={warrantyTypeBody}
                    sortable
                    style={{ minWidth: '150px' }}
                />

                {!isSapTab && isWarrantyTeam && (
                    <Column
                        field="submittedDate"
                        header={th('warrantyTeam.submittedDate')}
                        body={(r) => dateOnlyBodyTemplate(r, 'submittedDate')}
                        sortable
                        style={{ minWidth: '120px' }}
                    />
                )}

                {isWarrantyTeam && activeTabId === TAB_IDS.RETURNED && (
                    <Column
                        field="returnedDate"
                        header={th('dealerInvoice.returnedDate')}
                        body={(r) => dateOnlyBodyTemplate(r, 'returnedDate')}
                        sortable
                        style={{ minWidth: '120px' }}
                    />
                )}

                {isWarrantyTeam && WAITING_DAYS_TABS.includes(activeTabId) && (
                    <Column
                        field="waitingDays"
                        header={th('warrantyTeam.waitingDays')}
                        sortable
                        align="center"
                        style={{ width: '100px' }}
                        body={waitingDaysBody}
                    />
                )}

                {/* Bayide bekleme: SAP kayıtları, iade edilenler ve Tüm GT Siparişleri */}
                {isWarrantyTeam && DEALER_WAITING_DAYS_TABS.includes(activeTabId) && (
                    <Column
                        field="dealerWaitingDays"
                        header={th('warrantyTeam.dealerWaitingDays')}
                        sortable
                        align="center"
                        style={{ width: '100px' }}
                        body={dealerWaitingDaysBody}
                    />
                )}

                {/* Status kolonu: SAP tabı hariç göster (Tüm GT Siparişleri dahil) */}
                {!isSapTab && (
                    <Column
                        field="status"
                        header={th('common.status') || 'Durum'}
                        body={statusBody}
                        align="center"
                        style={{ width: '150px' }}
                    />
                )}
            </DataTable>

            <Dialog
                visible={actionDialog.visible}
                style={{ width: '450px' }}
                header={
                    actionDialog.type === 'approve'
                        ? th('warrantyTeam.approveDialog.title')
                        : actionDialog.type === 'reject'
                            ? th('warrantyTeam.rejectDialog.title')
                            : th('warrantyTeam.returnDialog.title')
                }
                modal
                onHide={closeActionDialog}
                footer={
                    <div>
                        <Button
                            label={th('common.cancel') || 'İptal'}
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={closeActionDialog}
                        />
                        <Button
                            label={th('common.confirm') || 'Onayla'}
                            icon="pi pi-check"
                            className={
                                actionDialog.type === 'reject' ? 'p-button-danger'
                                    : actionDialog.type === 'return' ? 'p-button-warning'
                                        : 'p-button-success'
                            }
                            onClick={confirmAction}
                            disabled={
                                (actionDialog.type === 'reject' || actionDialog.type === 'return')
                                && !actionReason.trim()
                            }
                        />
                    </div>
                }
            >
                <div className="flex flex-column gap-3">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-exclamation-circle text-3xl text-primary" />
                        <span>
                            {actionDialog.type === 'approve' && th('warrantyTeam.approveDialog.message')}
                            {actionDialog.type === 'reject' && th('warrantyTeam.rejectDialog.message')}
                            {actionDialog.type === 'return' && th('warrantyTeam.returnDialog.message')}
                        </span>
                    </div>
                    <div className="field">
                        <label className="font-bold">
                            {actionDialog.type === 'approve'
                                ? th('warrantyTeam.approveDialog.note')
                                : th('dealerInvoice.messages.reasonRequired')}
                            {actionDialog.type !== 'approve' && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <InputTextarea
                            value={actionReason}
                            onChange={(e) => setActionReason(e.target.value)}
                            rows={3}
                            className="w-full"
                            autoFocus
                            placeholder={th('dealerInvoice.reasonPlaceholder')}
                        />
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default React.memo(DealerInvoiceTable);
